import React, { useState, useMemo, useEffect } from 'react'

import {
  Stack,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import { CreditCard, Pix, PaymentOutlined, AddOutlined } from '@mui/icons-material'

import { ChargingStepProps, StartChargingStep } from '../hooks/useStartCharging/types'
import { CardSelector } from '../components/CardSelector'
import { ChargingStepActions } from '../components/ChargingStepActions'
import { numericUtils } from '@/utils/numeric'
import { ChargingPaymentMethod, PaymentMethodEnum } from '@/types/Payment'

export const PaymentMethodStep: React.FC<ChargingStepProps> = ({ startChargingMethods }) => {
  const { state, creditCards, setPaymentMethod, goBack, goToStep, balance } = startChargingMethods
  const { useWalletBalance, amount } = state

  const [selectedMethod, setSelectedMethod] = useState<ChargingPaymentMethod | null>(state.paymentMethod)
  const [selectedCardId, setSelectedCardId] = useState<number | null>(state.selectedCardId)

  useEffect(() => {
    setSelectedMethod(state.paymentMethod)
    setSelectedCardId(state.selectedCardId)
  }, [state.paymentMethod, state.selectedCardId])

  const showCardSelection = selectedMethod === PaymentMethodEnum.CREDIT_CARD

  const canContinue = useMemo(() => {
    if (!selectedMethod) return false
    if (selectedMethod === PaymentMethodEnum.CREDIT_CARD && !selectedCardId) return false
    return true
  }, [selectedMethod, selectedCardId])

  const handlePaymentMethodChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMethod: ChargingPaymentMethod | null
  ) => {
    setSelectedMethod(newMethod)
    setSelectedCardId(null)
  }

  const handleContinue = () => {
    if (canContinue && selectedMethod) {
      setPaymentMethod(selectedMethod, selectedCardId ?? undefined)
    }
  }

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PaymentOutlined color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Forma de pagamento
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <Box
            sx={{
              backgroundColor: 'action.hover',
              borderRadius: 2,
              padding: 2,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Valor a pagar:
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                {useWalletBalance
                  ? numericUtils.formatNumberToBRLCurrency(Number(amount) - Number(balance))
                  : numericUtils.formatNumberToBRLCurrency(amount ?? 0)}
              </Typography>
            </Stack>
          </Box>

          <ToggleButtonGroup
            value={selectedMethod}
            onChange={handlePaymentMethodChange}
            exclusive
            fullWidth
            aria-label="Método de pagamento"
          >
            <ToggleButton value={PaymentMethodEnum.PIX} aria-label="Pix" sx={{ paddingX: 2, gap: 1 }}>
              <Pix fontSize="small" />
              Pix
            </ToggleButton>
            <ToggleButton
              value={PaymentMethodEnum.CREDIT_CARD}
              aria-label="Cartão de Crédito"
              sx={{ paddingX: 2, gap: 1, whiteSpace: 'nowrap' }}
            >
              <CreditCard fontSize="small" />
              Cartão
            </ToggleButton>
          </ToggleButtonGroup>

          {showCardSelection && (
            <Stack spacing={2}>
              <Stack direction="column" spacing={0.5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Selecione um cartão de crédito:
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddOutlined />}
                  onClick={() => goToStep(StartChargingStep.ADD_CARD)}
                  sx={{ width: '100%', borderRadius: 9999, whiteSpace: 'nowrap' }}
                >
                  Adicionar cartão
                </Button>
              </Stack>

              {creditCards && creditCards?.results?.length > 0 ? (
                <CardSelector
                  cards={creditCards.results}
                  selectedCardId={selectedCardId}
                  onSelect={setSelectedCardId}
                />
              ) : (
                <Box
                  sx={{
                    backgroundColor: 'action.hover',
                    borderRadius: 2,
                    padding: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Você não possui cartões cadastrados.
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <ChargingStepActions sx={{ width: '100%' }}>
          <ChargingStepActions.ForwardButton onClick={handleContinue} disabled={!canContinue} />
          <ChargingStepActions.BackwardButton onClick={goBack} />
        </ChargingStepActions>
      </DialogActions>
    </>
  )
}
